import AdapterError, {
  AbortError as AdapterAbortError,
  ServerError as AdapterServerError,
  TimeoutError as AdapterTimeoutError,
} from '@ember-data/adapter/error';
import { AbortError, ServerError, TimeoutError } from 'garaje/services/ajax/errors';
import type MetricsService from 'garaje/services/metrics';
import { getStatusCode } from 'garaje/utils/parse-error';

// Default request timeout for the envoy-spaces and envoy-service-maps
// transports, so a hung service surfaces as a timeout error instead of
// stranding a page in a loading state.
export const SERVICE_REQUEST_TIMEOUT_MS = 20000;

interface CatchServiceErrorOptions {
  event: string;
  metrics?: MetricsService;
  debugExtras?: Record<string, unknown>;
}

/**
 * True when the error means a backend service is unavailable (5xx, timeout,
 * abort, or network failure) on either the ajax-fetch or Ember Data transport.
 * 4xx errors are excluded: those carry request-level semantics (auth,
 * permissions, validation) that callers must not paper over.
 */
export function isServiceAvailabilityError(error: unknown): boolean {
  if (error instanceof ServerError || error instanceof TimeoutError || error instanceof AbortError) {
    return true;
  }

  if (error instanceof AdapterError) {
    if (
      error instanceof AdapterServerError ||
      error instanceof AdapterTimeoutError ||
      error instanceof AdapterAbortError
    ) {
      return true;
    }
    // generic AdapterError: fall back to the status in the JSON:API payload
    const status = getStatusCode(error) as number | null;
    return typeof status === 'number' && status >= 500;
  }

  // fetch aborted via AbortController (e.g. ajax-fetch's options.timeout)
  if (error instanceof DOMException && error.name === 'AbortError') return true;

  // fetch rejects with a bare TypeError when the service is unreachable
  if (error instanceof TypeError && /fetch|network|load failed/i.test(error.message)) return true;

  return false;
}

/**
 * Builds an availability error a route can throw to land on its error
 * substate with the connectivity copy, e.g. when degraded data would
 * otherwise be mistaken for an empty state. (The @ember-data/adapter/error
 * type declarations only accept a message, but at runtime these errors carry
 * a JSON:API errors array, which is what getStatusCode reads.)
 */
export function serviceUnavailableError(message: string): AdapterError {
  const error = new AdapterServerError(message);
  (error as unknown as { errors: { status: string }[] }).errors = [{ status: '503' }];
  return error;
}

/**
 * Narrow availability-error swallow for *optional* fetches, so one backend
 * service being down degrades a single panel instead of rejecting the route
 * and tripping the global error page. Rethrows everything else. Pass the
 * caller's metrics service to report the degradation to Datadog.
 */
export default function catchServiceError<T, F>(
  promise: Promise<T>,
  fallback: F,
  { event, metrics, debugExtras }: CatchServiceErrorOptions,
): Promise<T | F> {
  return promise.catch((error: unknown) => {
    if (!isServiceAvailabilityError(error)) throw error;

    metrics?.logMonitorError({ event, debugExtras, error });
    return fallback;
  });
}
